import { useEffect, useState } from 'react';

import { appColors, BackrsIcon, SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { IntroSlide } from '../hooks/IntroSlidesPage.hooks';

const INTRO_STRINGS = appStrings.modules.intro;

type IntroSlidesNavFooterProps = Readonly<{
  currentSlide: number;
  setCurrentSlide: (slide: number) => void;
  slides: IntroSlide[];
}>;

export const IntroSlidesNavFooter = ({
  currentSlide,
  setCurrentSlide,
  slides,
}: IntroSlidesNavFooterProps) => {
  // we will only show the text explanation on the first slide, and only the first time you're on slide 0
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    if (currentSlide > 0) {
      setIsExpanded(false);
    }
  }, [currentSlide, setCurrentSlide]);

  const handleNextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <Box sx={navigationButtonSx}>
      <Box onClick={handlePrevSlide} sx={{ cursor: currentSlide === 0 ? 'default' : 'pointer' }}>
        <BackrsIcon
          name="IconLineArrowLeft"
          alt="left arrow"
          height={16}
          width={16}
          color={`${appColors.neutral.white}${currentSlide === 0 ? '70' : ''}`}
        />
      </Box>

      {/* TEXT EXPLANATION (ONLY FIRST SLIDE AND ON DESKTOP) */}
      {isExpanded ? <Typography sx={navigationTextSx}> {INTRO_STRINGS.navigation}</Typography> : ''}

      <Box
        onClick={() => {
          handleNextSlide();
          setIsExpanded(false);
        }}
        sx={{ cursor: currentSlide === slides.length ? 'default' : 'pointer' }}
      >
        <BackrsIcon
          name="IconLineArrowRight"
          alt="right arrow"
          height={16}
          width={16}
          color={`${appColors.neutral.white}${currentSlide === slides.length - 1 ? '70' : ''}`}
        />
      </Box>
    </Box>
  );
};

const navigationButtonSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: { xs: '16px', sm: '48px' },
  left: `calc(50% + ${SIDEBAR_COLLAPSED_WIDTH}px / 2)`,
  transform: 'translateX(-50%)',
  backgroundColor: appColors.neutral.black,
  color: appColors.neutral.white,
  paddingY: { xs: 1, sm: 2 },
  '@media (max-height: 768px)': {
    paddingY: '8px',
  },
  paddingX: 2,
  borderRadius: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  gap: 4,
} as const;

const navigationTextSx: SxProps<Theme> = {
  textTransform: 'none',
  userSelect: 'none',
  fontWeight: 600,
  lineHeight: '16px',
  fontSize: '16px',
  pb: '2px',
  color: appColors.neutral.white,
  textAlign: 'center',
  display: {
    xs: 'none',
    sm: 'block',
  },
} as const;
